import { AddLeadConnectIntegrationMutationVariables, LeadConnectTestCallResult } from "generated/graphql";

/**
 * The Lead Connect setup form's rules and defaults, kept apart from the dialog so they can be
 * tested on their own. The rules mirror stentor-api's validateLeadConnectParams: anything that
 * passes here is accepted there.
 */

/** CNAM carries at most 15 characters: A-Z, 0-9 and spaces. */
export const CALLER_ID_NAME_MAX = 15;
const CALLER_ID_NAME = /^[A-Z0-9 ]+$/;

/** 1 to 3 numbers, dialled in order, to reach the business. */
export const DISPATCH_NUMBERS_MAX = 3;

const E164_US = /^\+1[2-9]\d{2}[2-9]\d{6}$/;
const AREA_CODE = /^[2-9]\d{2}$/;
const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;

export type LeadConnectWeekday = "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat";

/** Indexed by Google's day number: 0 is Sunday. */
export const LEAD_CONNECT_WEEKDAYS: LeadConnectWeekday[] = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export const LEAD_CONNECT_WEEKDAY_LABELS: Record<LeadConnectWeekday, string> = {
    sun: "Sunday",
    mon: "Monday",
    tue: "Tuesday",
    wed: "Wednesday",
    thu: "Thursday",
    fri: "Friday",
    sat: "Saturday",
};

/** Which leads are called through. The analyzer's values; the rest of its list never warrants a call. */
export const LEAD_CONNECT_URGENCIES = [
    { label: "Urgent", value: "URGENT" },
    { label: "High", value: "HIGH" },
    { label: "Medium", value: "MEDIUM" },
    { label: "Low", value: "LOW" },
];

export const LEAD_CONNECT_CONTACT_TYPES = [
    { label: "Service Request", value: "SERVICE_REQUEST" },
    { label: "Quote Request", value: "QUOTE_REQUEST" },
    { label: "Contact Request", value: "CONTACT_REQUEST" },
];

export type LeadConnectCardState = "unsubscribed" | "setup" | "status";

export interface LeadConnectDayHours {
    /** HH:mm */
    open: string;
    /** HH:mm */
    close: string;
}

export interface LeadConnectDispatchNumberValue {
    label: string;
    /** As typed: any US format. Sent as E.164. */
    number: string;
}

export interface LeadConnectFormValues {
    /** Preferred area code for the Lead Connect number. Blank lets Lead Connect choose. */
    areaCode: string;
    callerIdName: string;
    dispatchNumbers: LeadConnectDispatchNumberValue[];
    /** Call during the location's hours. When on, no hours are sent and the API reads the location's. */
    useBusinessHours: boolean;
    /** IANA time zone for custom hours. */
    timeZone: string;
    weekly: Partial<Record<LeadConnectWeekday, LeadConnectDayHours>>;
    highValueOnly: boolean;
    urgencies: string[];
    contactTypes: string[];
}

export interface LeadConnectFormErrors {
    areaCode?: string;
    callerIdName?: string;
    /** About the list as a whole: how many there are. */
    dispatchNumbers?: string;
    /** One per dispatch number, in order. */
    dispatchNumberErrors?: (string | undefined)[];
    hours?: string;
}

/** The location fields the defaults come from: the shape of leadConnectStatus's app. */
export interface LeadConnectLocation {
    name?: string | null;
    leadsContactPhone?: string | null;
    places?: Array<{
        default?: boolean | null;
        phone?: string | null;
        openingHours?: {
            periods?: Array<{
                open: { day: number; time: string };
                close?: { day: number; time: string } | null;
            }> | null;
        } | null;
    }> | null;
}

export type LeadConnectAddOnStatus = string | null | undefined;

/** Upper-cases as the customer types. Characters CNAM cannot carry are left for validation to name. */
export function formatCallerIdName(input: string): string {
    return (input || "").toUpperCase();
}

export function validateCallerIdName(name: string): string | undefined {
    const trimmed = (name || "").trim();
    if (!trimmed) {
        return "Enter the name businesses see when Lead Connect calls.";
    }
    if (trimmed.length > CALLER_ID_NAME_MAX) {
        return `Caller ID names are at most ${CALLER_ID_NAME_MAX} characters.`;
    }
    if (!CALLER_ID_NAME.test(trimmed)) {
        return "Use only letters, numbers and spaces.";
    }
    return undefined;
}

/** A US number in any common format, as E.164 (+18045550100). Undefined when it is not one. */
export function toE164(input: string): string | undefined {
    const value = (input || "").trim();
    if (!value) {
        return undefined;
    }
    const digits = value.replace(/\D/g, "");
    // An explicit country code must be +1.
    if (value.startsWith("+") && !digits.startsWith("1")) {
        return undefined;
    }
    const national = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    const e164 = `+1${national}`;
    return E164_US.test(e164) ? e164 : undefined;
}

/** The NANP area code of a US phone number in any common format. */
export function areaCodeOf(phone: string | null | undefined): string | undefined {
    return toE164(phone || "")?.slice(2, 5);
}

/** The same derivation the API uses when no name is given, so the default always fits. */
function callerIdNameFrom(name: string | null | undefined): string {
    return (name || "")
        .toUpperCase()
        .replace(/[^A-Z0-9 ]/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, CALLER_ID_NAME_MAX)
        .trim();
}

function toHHmm(time: string): string {
    const digits = (time || "").replace(/\D/g, "").padStart(4, "0");
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
}

/** Weekday office hours, for a location without any. */
function defaultWeekly(): LeadConnectFormValues["weekly"] {
    const weekly: LeadConnectFormValues["weekly"] = {};
    (["mon", "tue", "wed", "thu", "fri"] as LeadConnectWeekday[]).forEach((day) => {
        weekly[day] = { open: "08:00", close: "17:00" };
    });
    return weekly;
}

/**
 * The location's Google hours as one call window per day. A day with split hours spans from the
 * first opening to the last close; a period open past midnight or round the clock runs to 23:59.
 */
function weeklyFromPlace(
    periods: NonNullable<NonNullable<LeadConnectLocation["places"]>[number]["openingHours"]>["periods"]
) {
    const weekly: LeadConnectFormValues["weekly"] = {};
    (periods || []).forEach((period) => {
        const day = LEAD_CONNECT_WEEKDAYS[period?.open?.day];
        if (!day) {
            return;
        }
        const open = toHHmm(period.open.time);
        const sameDayClose = period.close && period.close.day === period.open.day;
        const close = sameDayClose ? toHHmm(period.close.time) : "23:59";
        const existing = weekly[day];
        weekly[day] = existing
            ? {
                  open: existing.open < open ? existing.open : open,
                  close: existing.close > close ? existing.close : close,
              }
            : { open, close };
    });
    return weekly;
}

function browserTimeZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch {
        return "";
    }
}

function isValidTimeZone(timeZone: string): boolean {
    if (!timeZone) {
        return false;
    }
    try {
        new Intl.DateTimeFormat("en-US", { timeZone });
        return true;
    } catch {
        return false;
    }
}

/**
 * The setup form's starting values, from the app's default location: its phone's area code (else
 * the leads phone's), a caller ID name cut from the business name, its phone as the first number to
 * dispatch to, and its hours.
 */
export function defaultLeadConnectForm(
    location: LeadConnectLocation,
    timeZone: string = browserTimeZone()
): LeadConnectFormValues {
    const places = location.places || [];
    const place = places.find((p) => p?.default) || places[0];
    const locationPhone = place?.phone || location.leadsContactPhone || "";
    const weekly = weeklyFromPlace(place?.openingHours?.periods);

    return {
        areaCode: areaCodeOf(place?.phone) || areaCodeOf(location.leadsContactPhone) || "",
        callerIdName: callerIdNameFrom(location.name),
        dispatchNumbers: [{ label: "Office", number: locationPhone }],
        useBusinessHours: true,
        timeZone,
        weekly: Object.keys(weekly).length > 0 ? weekly : defaultWeekly(),
        highValueOnly: true,
        urgencies: [],
        contactTypes: [],
    };
}

export function validateLeadConnectForm(values: LeadConnectFormValues): LeadConnectFormErrors {
    const errors: LeadConnectFormErrors = {};

    const areaCode = (values.areaCode || "").trim();
    if (areaCode && !AREA_CODE.test(areaCode)) {
        errors.areaCode = "A 3-digit US area code, like 804.";
    }

    errors.callerIdName = validateCallerIdName(values.callerIdName);

    const numbers = values.dispatchNumbers || [];
    if (numbers.length < 1 || numbers.length > DISPATCH_NUMBERS_MAX) {
        errors.dispatchNumbers = `Add 1 to ${DISPATCH_NUMBERS_MAX} numbers to reach the business.`;
    }
    const numberErrors = numbers.map((dispatch) => {
        if (!toE164(dispatch.number)) {
            return "Enter a US phone number, like (804) 555-0100.";
        }
        if (!dispatch.label?.trim()) {
            return "Give this number a label, like Office or On-call.";
        }
        return undefined;
    });
    if (numberErrors.some((error) => !!error)) {
        errors.dispatchNumberErrors = numberErrors;
    }

    if (!values.useBusinessHours) {
        const days = LEAD_CONNECT_WEEKDAYS.filter((day) => values.weekly?.[day]);
        if (!isValidTimeZone(values.timeZone)) {
            errors.hours = "Enter a time zone, like America/New_York.";
        } else if (days.length === 0) {
            errors.hours = "Pick at least one day to take calls.";
        } else {
            const bad = days.find((day) => {
                const { open, close } = values.weekly[day] as LeadConnectDayHours;
                return !HH_MM.test(open || "") || !HH_MM.test(close || "") || open >= close;
            });
            if (bad) {
                errors.hours = `${LEAD_CONNECT_WEEKDAY_LABELS[bad]} must open before it closes.`;
            }
        }
    }

    // Keep only the errors that are set, so an empty object means a valid form.
    return Object.fromEntries(
        Object.entries(errors).filter(([, value]) => value !== undefined)
    ) as LeadConnectFormErrors;
}

export function hasFormErrors(errors: LeadConnectFormErrors): boolean {
    return Object.keys(errors).length > 0;
}

/**
 * The addLeadConnectIntegration variables for a valid form. No callerIdNumber: Lead Connect
 * provides the number. Hours are sent only when the business set its own.
 */
export function toAddLeadConnectVariables(
    values: LeadConnectFormValues
): Omit<AddLeadConnectIntegrationMutationVariables, "appId"> {
    const areaCode = (values.areaCode || "").trim();
    const weekly: LeadConnectFormValues["weekly"] = {};
    LEAD_CONNECT_WEEKDAYS.forEach((day) => {
        if (values.weekly?.[day]) {
            weekly[day] = values.weekly[day];
        }
    });

    return {
        ...(areaCode && { areaCode }),
        callerIdName: values.callerIdName.trim(),
        dispatchNumbers: values.dispatchNumbers.map((dispatch) => ({
            label: dispatch.label.trim(),
            number: toE164(dispatch.number) as string,
        })),
        criteria: {
            highValueOnly: values.highValueOnly,
            ...(values.urgencies.length > 0 && { urgencies: values.urgencies }),
            ...(values.contactTypes.length > 0 && { contactTypes: values.contactTypes }),
        },
        ...(!values.useBusinessHours && { hours: { timeZone: values.timeZone, weekly } }),
    };
}

/**
 * Which face the card shows. A set-up integration shows its status even after the add-on lapses,
 * so the business can still see the number being released.
 */
export function getLeadConnectCardState(addOnStatus: LeadConnectAddOnStatus, isLinked: boolean): LeadConnectCardState {
    if (isLinked) {
        return "status";
    }
    return addOnStatus === "active" ? "setup" : "unsubscribed";
}

/** The toast for "Call me with a sample lead". */
export function leadConnectTestCallMessage(
    result: Pick<LeadConnectTestCallResult, "accepted" | "reason"> & Partial<LeadConnectTestCallResult>
): { message: string; isError: boolean } {
    if (result.accepted) {
        return { message: "Lead Connect is calling your first number now with a sample lead.", isError: false };
    }
    return {
        message: `Lead Connect could not place the test call. ${result.reason || "Try again in a few minutes."}`,
        isError: true,
    };
}
